/**
 * Query editor pane (WS-1) — the FULL-HEIGHT editor for ONE page query, shown in
 * the main workspace area when a Query tab is active. Lifted out of the bottom
 * `QueryDock` (which crammed it into a ~200px strip): the same `QueryForm` plus a
 * result-preview table, now with all the room a query author needs. Owns only
 * the dynamic-preview run state; the query itself is edited through `onChange`.
 */
import * as React from "react";

import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import type { PageQuery } from "@/lib/pageTypes";

import { QueryForm } from "./QueryForm";

export interface QueryPreview {
  rows: Record<string, unknown>[];
  total: number;
}

export function QueryEditorPane({
  query,
  tables,
  datasets = [],
  columnsByTable,
  pageVarNames,
  appQueries = [],
  preview,
  onChange,
  onRemove,
  onRunPreview,
}: {
  query: PageQuery;
  tables: { id: string; name: string }[];
  datasets?: { id: string; name: string }[];
  columnsByTable: Record<string, string[]>;
  pageVarNames: string[];
  /** Named app-level Queries selectable as a `query` reference (ADR-0047). */
  appQueries?: { id: string; key: string; name: string }[];
  /** Static preview (table/dataset/aggregate) for THIS query, resolved by the
   *  parent from the page preview map. */
  preview?: QueryPreview;
  onChange: (next: Partial<PageQuery>) => void;
  onRemove: () => void;
  /** Server-side preview for http/js sources (run on an explicit click). */
  onRunPreview?: (q: PageQuery) => Promise<QueryPreview | null>;
}) {
  // http/js/query resolve server-side → previewed on an explicit "Esegui" click,
  // not on every keystroke. table/dataset/aggregate come from the page preview
  // map. A `query` reference (ADR-0047) is resolved server-side too (it may point
  // at a read app-Query), so it joins the dynamic set.
  const isDynamic = ["http", "js", "query"].includes(query.source_kind ?? "table");
  const [ran, setRan] = React.useState<QueryPreview | null>(null);
  const [running, setRunning] = React.useState(false);
  const [runError, setRunError] = React.useState<string | null>(null);

  // Switching to a different query (the pane is reused across tabs) drops the
  // stale dynamic-run cache.
  React.useEffect(() => {
    setRan(null);
    setRunError(null);
  }, [query.id]);

  const runPreview = async () => {
    if (!onRunPreview) return;
    setRunning(true);
    setRunError(null);
    try {
      const res = await onRunPreview(query);
      if (res) setRan(res);
    } catch (e) {
      setRunError(e instanceof Error ? e.message : "Anteprima fallita");
    } finally {
      setRunning(false);
    }
  };

  const prev = isDynamic ? ran : preview;
  const rows = prev?.rows ?? [];
  const cols = rows[0] ? Object.keys(rows[0]) : [];

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto p-4">
      <QueryForm
        query={query}
        tables={tables}
        datasets={datasets}
        columnsByTable={columnsByTable}
        pageVarNames={pageVarNames}
        appQueries={appQueries}
        onChange={onChange}
        onRemove={onRemove}
      />

      <div className="mt-4 border-t border-border/50 pt-3">
        <div className="mb-1.5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          Anteprima
          <span className="font-normal normal-case text-muted-foreground">
            {prev ? `${prev.total} righe` : "—"}
          </span>
          {isDynamic && onRunPreview && (
            <button
              type="button"
              onClick={runPreview}
              disabled={running}
              className="ml-auto rounded border border-accent/40 bg-accent/10 px-2 py-0.5 text-[10px] font-medium normal-case text-accent hover:bg-accent/20 disabled:opacity-50"
            >
              {running ? "Esecuzione…" : "Esegui anteprima"}
            </button>
          )}
        </div>
        {runError && <p className="mb-1 text-[11px] text-danger">{runError}</p>}
        {rows.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">
            {isDynamic
              ? "Premi «Esegui anteprima» per eseguire la richiesta."
              : "Nessun dato di anteprima."}
          </p>
        ) : (
          <div className="overflow-auto rounded border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  {cols.map((c) => (
                    <Th key={c} className="whitespace-nowrap text-[10px]">
                      {c}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {rows.slice(0, 50).map((row, i) => (
                  <Tr key={i}>
                    {cols.map((c) => (
                      <Td key={c} className="whitespace-nowrap text-[11px]">
                        {fmtCell(row[c])}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

/** Compact, non-crashing cell render for the preview table. */
function fmtCell(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return Array.isArray(v) ? `[${v.length}]` : "{…}";
  const s = String(v);
  return s.length > 60 ? `${s.slice(0, 59)}…` : s;
}
